import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Chip,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Tab,
    Tabs,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    alpha,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import MeetingRoomOutlinedIcon from "@mui/icons-material/MeetingRoomOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import { getApiBaseUrl } from "../../../config/api";
import Popup from "../../../components/Popup";
import {
    HOSTEL_GENDERS,
    hostelGenderFormValue,
    studentGenderToHostelGender,
} from "../../../utils/hostelGenderUtils";

const GENDERS = HOSTEL_GENDERS;

function Kpi({ label, value, sub }) {
    return (
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}`, height: "100%" }}>
            <Typography variant="overline" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="h4" fontWeight={800}>
                {value}
            </Typography>
            {sub ? (
                <Typography variant="caption" color="text.secondary">
                    {sub}
                </Typography>
            ) : null}
        </Paper>
    );
}

const HostelManagement = ({ defaultTab = 0 }) => {
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = currentUser?._id;

    const [tab, setTab] = useState(defaultTab);
    const [hostels, setHostels] = useState([]);
    const [overview, setOverview] = useState(null);
    const [boarders, setBoarders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");
    const [showPopup, setShowPopup] = useState(false);

    const [hostelDialog, setHostelDialog] = useState(false);
    const [roomDialog, setRoomDialog] = useState(false);
    const [assignDialog, setAssignDialog] = useState(false);
    const [editingHostel, setEditingHostel] = useState(null);
    const [selectedHostelId, setSelectedHostelId] = useState("");
    const [assignTarget, setAssignTarget] = useState(null);

    const [hostelForm, setHostelForm] = useState({ name: "", gender: "Boys", capacity: "", wardenName: "", wardenPhone: "", notes: "" });
    const [roomForm, setRoomForm] = useState({ roomNumber: "", capacity: "4", floor: "" });
    const [assignForm, setAssignForm] = useState({ hostelRoomId: "", bedLabel: "" });

    const allRooms = useMemo(
        () =>
            hostels.flatMap((h) =>
                (h.rooms || []).map((r) => ({
                    ...r,
                    hostelName: h.name,
                    hostelId: h._id || h.id,
                    hostelGender: h.gender,
                }))
            ),
        [hostels]
    );

    const assignableRooms = useMemo(() => {
        if (!assignTarget) return allRooms;
        const required = studentGenderToHostelGender(assignTarget.gender);
        if (!required) return [];
        return allRooms.filter((r) => hostelGenderFormValue(r.hostelGender) === required);
    }, [allRooms, assignTarget]);

    const load = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        try {
            const base = getApiBaseUrl();
            const [hostelRes, overviewRes, boarderRes] = await Promise.all([
                axios.get(`${base}/Hostels/${schoolId}`),
                axios.get(`${base}/HostelOverview/${schoolId}`),
                axios.get(`${base}/HostelBoarders/${schoolId}`),
            ]);
            setHostels(Array.isArray(hostelRes.data) ? hostelRes.data : []);
            setOverview(overviewRes.data || null);
            setBoarders(Array.isArray(boarderRes.data) ? boarderRes.data : []);
        } catch {
            setHostels([]);
            setOverview(null);
            setBoarders([]);
        } finally {
            setLoading(false);
        }
    }, [schoolId]);

    useEffect(() => {
        load();
    }, [load]);

    const openHostelDialog = (hostel = null) => {
        setEditingHostel(hostel);
        setHostelForm(
            hostel
                ? {
                      name: hostel.name || "",
                      gender: hostelGenderFormValue(hostel.gender),
                      capacity: hostel.capacity || "",
                      wardenName: hostel.wardenName || "",
                      wardenPhone: hostel.wardenPhone || "",
                      notes: hostel.notes || "",
                  }
                : { name: "", gender: "Boys", capacity: "", wardenName: "", wardenPhone: "", notes: "" }
        );
        setHostelDialog(true);
    };

    const saveHostel = async () => {
        const base = getApiBaseUrl();
        try {
            if (editingHostel) {
                const id = editingHostel._id || editingHostel.id;
                const res = await axios.put(`${base}/Hostel/${id}`, { ...hostelForm, schoolId });
                if (res.data?.message && !res.data?.name) {
                    setMessage(res.data.message);
                    setShowPopup(true);
                    return;
                }
            } else {
                const res = await axios.post(`${base}/Hostel`, { ...hostelForm, schoolId });
                if (res.data?.message && !res.data?.name) {
                    setMessage(res.data.message);
                    setShowPopup(true);
                    return;
                }
            }
            setHostelDialog(false);
            load();
        } catch (err) {
            const detail = err.response?.data?.message || err.message || "Could not save hostel";
            setMessage(detail);
            setShowPopup(true);
        }
    };

    const deleteHostel = async (hostel) => {
        if (!window.confirm(`Delete hostel "${hostel.name}"?`)) return;
        const res = await axios.delete(`${getApiBaseUrl()}/Hostel/${hostel._id || hostel.id}`);
        if (res.data?.message && res.data.message !== "Hostel deleted") {
            setMessage(res.data.message);
            setShowPopup(true);
            return;
        }
        load();
    };

    const openRoomDialog = (hostelId = "") => {
        setSelectedHostelId(hostelId || hostels[0]?._id || hostels[0]?.id || "");
        setRoomForm({ roomNumber: "", capacity: "4", floor: "" });
        setRoomDialog(true);
    };

    const saveRoom = async () => {
        const res = await axios.post(`${getApiBaseUrl()}/HostelRoom`, {
            ...roomForm,
            hostelId: selectedHostelId,
            capacity: Number(roomForm.capacity) || 4,
        });
        if (res.data?.message && !res.data?.roomNumber) {
            setMessage(res.data.message);
            setShowPopup(true);
            return;
        }
        setRoomDialog(false);
        load();
    };

    const deleteRoom = async (room) => {
        if (!window.confirm(`Delete room ${room.roomNumber}?`)) return;
        const res = await axios.delete(`${getApiBaseUrl()}/HostelRoom/${room._id || room.id}`);
        if (res.data?.message && res.data.message !== "Room deleted") {
            setMessage(res.data.message);
            setShowPopup(true);
            return;
        }
        load();
    };

    const openAssignDialog = (student) => {
        setAssignTarget(student);
        setAssignForm({
            hostelRoomId: student.hostelRoom?._id || student.hostelRoom?.id || "",
            bedLabel: student.bedLabel || "",
        });
        setAssignDialog(true);
    };

    const saveAssignment = async () => {
        if (!assignTarget) return;
        const res = await axios.put(`${getApiBaseUrl()}/StudentHostelAssignment/${assignTarget._id || assignTarget.id}`, {
            residenceType: "Boarding",
            hostelRoomId: assignForm.hostelRoomId || null,
            bedLabel: assignForm.bedLabel,
        });
        if (res.data?.message && !res.data?.name) {
            setMessage(res.data.message);
            setShowPopup(true);
            return;
        }
        setAssignDialog(false);
        load();
        if (res.data?.feeNotice) {
            setMessage(res.data.feeNotice);
            setShowPopup(true);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[100]} 20%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="lg">
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }}>
                    <HotelOutlinedIcon color="primary" />
                    <Typography variant="h5" fontWeight={800}>
                        Hostel management
                    </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Manage dormitories, rooms, and boarder assignments for your school.
                </Typography>

                {overview ? (
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={6} sm={3}>
                            <Kpi label="Hostels" value={overview.hostelCount} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Kpi label="Boarders" value={overview.boarderCount} sub={`${overview.unassignedBoarders} unassigned`} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Kpi label="Total beds" value={overview.totalBeds} />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Kpi label="Vacant beds" value={overview.vacantBeds} sub={`${overview.occupiedBeds} occupied`} />
                        </Grid>
                    </Grid>
                ) : null}

                <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                    <Tab icon={<HotelOutlinedIcon />} iconPosition="start" label="Hostels" sx={{ textTransform: "none", fontWeight: 600 }} />
                    <Tab icon={<MeetingRoomOutlinedIcon />} iconPosition="start" label="Rooms" sx={{ textTransform: "none", fontWeight: 600 }} />
                    <Tab icon={<PeopleOutlineIcon />} iconPosition="start" label="Boarders" sx={{ textTransform: "none", fontWeight: 600 }} />
                </Tabs>

                {tab === 0 && (
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography fontWeight={700}>Hostel blocks</Typography>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openHostelDialog()}>
                                Add hostel
                            </Button>
                        </Stack>
                        {loading ? (
                            <Typography color="text.secondary">Loading…</Typography>
                        ) : hostels.length === 0 ? (
                            <Alert severity="info">No hostels yet. Add your first dormitory block.</Alert>
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Name</TableCell>
                                            <TableCell>Gender</TableCell>
                                            <TableCell>Rooms</TableCell>
                                            <TableCell>Occupancy</TableCell>
                                            <TableCell>Warden</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {hostels.map((h) => (
                                            <TableRow key={h._id || h.id}>
                                                <TableCell>{h.name}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={
                                                            h.gender === "Girls"
                                                                ? "Girls only"
                                                                : h.gender === "Boys"
                                                                  ? "Boys only"
                                                                  : "Set gender"
                                                        }
                                                        color={h.gender === "Girls" ? "secondary" : h.gender === "Boys" ? "primary" : "warning"}
                                                    />
                                                </TableCell>
                                                <TableCell>{h.roomCount}</TableCell>
                                                <TableCell>
                                                    {h.occupied}/{h.totalCapacity || "—"}
                                                    {h.vacant > 0 ? (
                                                        <Typography variant="caption" display="block" color="text.secondary">
                                                            {h.vacant} vacant
                                                        </Typography>
                                                    ) : null}
                                                </TableCell>
                                                <TableCell>
                                                    {h.wardenName || "—"}
                                                    {h.wardenPhone ? (
                                                        <Typography variant="caption" display="block">
                                                            {h.wardenPhone}
                                                        </Typography>
                                                    ) : null}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button size="small" onClick={() => openHostelDialog(h)}>
                                                        Edit
                                                    </Button>
                                                    <Button size="small" color="error" onClick={() => deleteHostel(h)}>
                                                        Delete
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                )}

                {tab === 1 && (
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography fontWeight={700}>Rooms</Typography>
                            <Button variant="contained" startIcon={<AddIcon />} onClick={() => openRoomDialog()} disabled={!hostels.length}>
                                Add room
                            </Button>
                        </Stack>
                        {!hostels.length ? (
                            <Alert severity="info">Create a hostel first, then add rooms.</Alert>
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Hostel</TableCell>
                                            <TableCell>Room</TableCell>
                                            <TableCell>Floor</TableCell>
                                            <TableCell>Capacity</TableCell>
                                            <TableCell>Occupied</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {allRooms.map((r) => (
                                            <TableRow key={r._id || r.id}>
                                                <TableCell>{r.hostelName}</TableCell>
                                                <TableCell>{r.roomNumber}</TableCell>
                                                <TableCell>{r.floor || "—"}</TableCell>
                                                <TableCell>{r.capacity}</TableCell>
                                                <TableCell>
                                                    {r.occupied}/{r.capacity}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button size="small" color="error" onClick={() => deleteRoom(r)}>
                                                        Delete
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                )}

                {tab === 2 && (
                    <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: (t) => `1px solid ${t.palette.divider}` }}>
                        <Typography fontWeight={700} sx={{ mb: 2 }}>
                            Boarding students
                        </Typography>
                        {boarders.length === 0 ? (
                            <Alert severity="info">
                                No boarders yet. Set students as boarders when enrolling them, then assign rooms here.
                            </Alert>
                        ) : (
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Student</TableCell>
                                            <TableCell>Class</TableCell>
                                            <TableCell>Hostel / Room</TableCell>
                                            <TableCell>Bed</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {boarders.map((s) => (
                                            <TableRow key={s._id || s.id}>
                                                <TableCell>{s.name}</TableCell>
                                                <TableCell>{s.sclassName?.sclassName || "—"}</TableCell>
                                                <TableCell>
                                                    {s.hostelRoom ? (
                                                        <>
                                                            {s.hostelRoom.hostel?.name} — {s.hostelRoom.roomNumber}
                                                        </>
                                                    ) : (
                                                        <Chip size="small" color="warning" label="Unassigned" />
                                                    )}
                                                </TableCell>
                                                <TableCell>{s.bedLabel || "—"}</TableCell>
                                                <TableCell align="right">
                                                    <Button size="small" onClick={() => openAssignDialog(s)}>
                                                        Assign room
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Paper>
                )}
            </Container>

            <Dialog open={hostelDialog} onClose={() => setHostelDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editingHostel ? "Edit hostel" : "Add hostel"}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Hostel name" fullWidth required value={hostelForm.name} onChange={(e) => setHostelForm((f) => ({ ...f, name: e.target.value }))} />
                        <FormControl fullWidth required>
                            <InputLabel>Gender</InputLabel>
                            <Select label="Gender" value={hostelForm.gender} onChange={(e) => setHostelForm((f) => ({ ...f, gender: e.target.value }))}>
                                {GENDERS.map((g) => (
                                    <MenuItem key={g} value={g}>
                                        {g === "Boys" ? "Boys only" : "Girls only"}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField label="Total capacity (optional)" type="number" fullWidth value={hostelForm.capacity} onChange={(e) => setHostelForm((f) => ({ ...f, capacity: e.target.value }))} />
                        <TextField label="Warden name" fullWidth value={hostelForm.wardenName} onChange={(e) => setHostelForm((f) => ({ ...f, wardenName: e.target.value }))} />
                        <TextField label="Warden phone" fullWidth value={hostelForm.wardenPhone} onChange={(e) => setHostelForm((f) => ({ ...f, wardenPhone: e.target.value }))} />
                        <TextField label="Notes" fullWidth multiline minRows={2} value={hostelForm.notes} onChange={(e) => setHostelForm((f) => ({ ...f, notes: e.target.value }))} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setHostelDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveHostel} disabled={!hostelForm.name.trim()}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={roomDialog} onClose={() => setRoomDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Add room</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth required>
                            <InputLabel>Hostel</InputLabel>
                            <Select label="Hostel" value={selectedHostelId} onChange={(e) => setSelectedHostelId(e.target.value)}>
                                {hostels.map((h) => (
                                    <MenuItem key={h._id || h.id} value={h._id || h.id}>
                                        {h.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField label="Room number" fullWidth required value={roomForm.roomNumber} onChange={(e) => setRoomForm((f) => ({ ...f, roomNumber: e.target.value }))} />
                        <TextField label="Floor" fullWidth value={roomForm.floor} onChange={(e) => setRoomForm((f) => ({ ...f, floor: e.target.value }))} />
                        <TextField label="Bed capacity" type="number" fullWidth value={roomForm.capacity} onChange={(e) => setRoomForm((f) => ({ ...f, capacity: e.target.value }))} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRoomDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveRoom} disabled={!selectedHostelId || !roomForm.roomNumber.trim()}>
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={assignDialog} onClose={() => setAssignDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Assign room — {assignTarget?.name}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {!assignTarget?.gender ? (
                            <Alert severity="warning" sx={{ borderRadius: 2 }}>
                                Set this student&apos;s gender (Male or Female) on their profile before assigning a hostel room.
                            </Alert>
                        ) : null}
                        <FormControl fullWidth required disabled={!assignTarget?.gender}>
                            <InputLabel>Room</InputLabel>
                            <Select label="Room" value={assignForm.hostelRoomId} onChange={(e) => setAssignForm((f) => ({ ...f, hostelRoomId: e.target.value }))}>
                                {assignableRooms.length === 0 ? (
                                    <MenuItem value="" disabled>
                                        No {studentGenderToHostelGender(assignTarget?.gender)?.toLowerCase() || ""} hostel rooms available
                                    </MenuItem>
                                ) : null}
                                {assignableRooms.map((r) => (
                                    <MenuItem key={r._id || r.id} value={r._id || r.id} disabled={r.vacant <= 0 && assignForm.hostelRoomId !== (r._id || r.id)}>
                                        {r.hostelName} — Room {r.roomNumber} ({r.occupied}/{r.capacity})
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField label="Bed label (optional)" fullWidth placeholder="e.g. Bed 2" value={assignForm.bedLabel} onChange={(e) => setAssignForm((f) => ({ ...f, bedLabel: e.target.value }))} />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAssignDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveAssignment} disabled={!assignForm.hostelRoomId}>
                        Assign
                    </Button>
                </DialogActions>
            </Dialog>

            <Popup message={message} setShowPopup={setShowPopup} showPopup={showPopup} />
        </Box>
    );
};

export default HostelManagement;
